/* ==========================================================================
   Aledo theme — global.js
   Vanilla ES2019+. No frameworks, no dependencies, no build step.
   Implements every custom element + helper described in the theme spec.
   Everything is DEFENSIVE: guard every lookup, never throw when an element
   is missing, and upgrade automatically when custom elements connect.

   SECURITY NOTE on innerHTML: markup injected via innerHTML comes only from
   (a) Shopify's Section Rendering API (server-rendered, Liquid-escaped) or
   (b) strings passed through the local escapeHTML() helper. No raw,
   unescaped user input is ever written to the DOM.
   ========================================================================== */
(function () {
  "use strict";

  /* --------------------------------------------------------------------------
     0. no-js -> js
     Swap the html class as early as possible so CSS can react.
     -------------------------------------------------------------------------- */
  try {
    var docEl = document.documentElement;
    docEl.classList.remove("no-js");
    docEl.classList.add("js");
  } catch (e) {
    /* ignore */
  }

  /* --------------------------------------------------------------------------
     1. Utilities
     -------------------------------------------------------------------------- */

  // Querying helpers — always scoped, never throw.
  function q(selector, root) {
    return (root || document).querySelector(selector);
  }
  function qa(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  // HTML-escape any untrusted string before it is used in innerHTML.
  function escapeHTML(str) {
    return String(str === undefined || str === null ? "" : str).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  // Classic debounce — leading edge off, trailing edge on.
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  // Build a fetch() options object for JSON POST / GET against Shopify AJAX API.
  function fetchConfig(type, body) {
    type = type || "json";
    var headers = {
      "Content-Type": "application/json",
      Accept: "application/json"
    };
    var config = { method: "POST", headers: headers };
    if (body !== undefined && body !== null) {
      config.body = typeof body === "string" ? body : JSON.stringify(body);
    }
    return config;
  }

  // Theme globals — exposed for sections / snippets that want them.
  var theme = (window.theme = window.theme || {});
  theme.moneyFormat = theme.moneyFormat || "${{amount}}";
  theme.routes = theme.routes || {};

  /*
   * Money formatting. Mirrors Shopify's money filter closely enough for
   * client-side re-rendering of prices after variant changes.
   * `cents` is an integer number of cents (as returned by /cart.js, variants).
   */
  function formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(/[^0-9.\-]/g, "");
    }
    var value = "";
    var fmt = format || theme.moneyFormat || "${{amount}}";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function defaultTo(num, def) {
      return typeof num === "number" && !isNaN(num) ? num : def;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = thousands || ",";
      decimal = decimal || ".";
      if (isNaN(number) || number === null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split(".");
      var dollarsPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : "";
      return dollarsPart + centsPart;
    }

    var match = fmt.match(placeholderRegex);
    var placeholder = match ? match[1] : "amount";

    switch (placeholder) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ", "");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return fmt.replace(placeholderRegex, value);
  }
  theme.formatMoney = formatMoney;

  /*
   * Fetch a URL and return the raw text body. Used for Section Rendering API
   * responses. Resolves to null on any failure so callers can bail gracefully.
   */
  function fetchSection(url) {
    return fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(function (res) {
        if (!res.ok) throw new Error("Bad response " + res.status);
        return res.text();
      })
      .catch(function () {
        return null;
      });
  }
  theme.fetchSection = fetchSection;

  // Parse an HTML string into a document we can query against.
  function parseHTML(text) {
    return new DOMParser().parseFromString(text, "text/html");
  }

  /*
   * Replace the [data-product-grid] (and optionally active-filter chips) inside
   * a target root from a freshly fetched document. Source markup is the
   * server-rendered (trusted) Section Rendering response.
   */
  function replaceProductGrid(htmlText, targetRoot) {
    if (!htmlText) return false;
    var newDoc = parseHTML(htmlText);
    var root = targetRoot || document;
    var swapped = false;

    var newGrid = newDoc.querySelector("[data-product-grid]");
    var oldGrid = root.querySelector("[data-product-grid]");
    if (newGrid && oldGrid) {
      oldGrid.innerHTML = newGrid.innerHTML;
      swapped = true;
    }

    var newActive = newDoc.querySelector("[data-facets-active]");
    var oldActive = root.querySelector("[data-facets-active]");
    if (newActive && oldActive) {
      oldActive.innerHTML = newActive.innerHTML;
    }

    return swapped;
  }

  // Refresh [data-cart-count] elements from a live /cart.js read.
  function refreshCartCount() {
    return fetch(theme.routes.cart_url ? theme.routes.cart_url + ".js" : "/cart.js", {
      headers: { Accept: "application/json" }
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart) return null;
        setCartCount(cart.item_count);
        return cart;
      })
      .catch(function () {
        return null;
      });
  }
  theme.refreshCartCount = refreshCartCount;

  function setCartCount(count) {
    qa("[data-cart-count]").forEach(function (el) {
      el.textContent = count;
      el.setAttribute("data-cart-count", String(count));
      // Hide an empty bubble if the markup opts in with [data-hide-empty].
      if (el.hasAttribute("data-hide-empty")) {
        el.hidden = count <= 0;
      }
    });
  }

  /* --------------------------------------------------------------------------
     2. Body scroll-lock shared across all drawers/modals
     Reference-counted so nested/multiple drawers don't fight each other.
     -------------------------------------------------------------------------- */
  var scrollLockCount = 0;
  var savedScrollY = 0;

  function lockScroll() {
    scrollLockCount += 1;
    if (scrollLockCount > 1) return;
    savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = "-" + savedScrollY + "px";
    document.body.classList.add("scroll-locked");
  }

  function unlockScroll() {
    if (scrollLockCount === 0) return;
    scrollLockCount -= 1;
    if (scrollLockCount > 0) return;
    document.body.classList.remove("scroll-locked");
    document.body.style.top = "";
    window.scrollTo(0, savedScrollY);
  }

  // Track open drawers so Escape closes only the topmost.
  var openDrawers = [];

  function registerOpenDrawer(el) {
    if (openDrawers.indexOf(el) === -1) openDrawers.push(el);
  }
  function unregisterOpenDrawer(el) {
    var i = openDrawers.indexOf(el);
    if (i !== -1) openDrawers.splice(i, 1);
  }

  // Simple focus trap helper for drawer panels.
  var FOCUSABLE =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, handlerStore) {
    if (!container) return;
    var focusable = qa(FOCUSABLE, container).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    function onKey(e) {
      if (e.key !== "Tab") return;
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        if (last) last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        if (first) first.focus();
      }
    }

    container.addEventListener("keydown", onKey);
    if (handlerStore) handlerStore._focusHandler = onKey;
    if (first) {
      // Defer focus so transitions/transforms settle first.
      window.setTimeout(function () {
        first.focus();
      }, 50);
    }
  }

  function releaseFocus(container, handlerStore) {
    if (container && handlerStore && handlerStore._focusHandler) {
      container.removeEventListener("keydown", handlerStore._focusHandler);
      handlerStore._focusHandler = null;
    }
  }

  /* --------------------------------------------------------------------------
     3. MenuDrawer — <menu-drawer>
     Mobile off-canvas navigation. A header button with [data-menu-toggle]
     (and aria-controls pointing at the drawer id) opens it.
     -------------------------------------------------------------------------- */
  var MenuDrawer = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    if (typeof HTMLElement === "function") {
      Object.setPrototypeOf(Ctor.prototype, HTMLElement.prototype);
      Object.setPrototypeOf(Ctor, HTMLElement);
    }

    Ctor.prototype.connectedCallback = function () {
      this.drawerEl = this.querySelector(".drawer") || this;
      this.panel = this.querySelector(".drawer__panel") || this;
      this.overlay = this.querySelector(".drawer__overlay");
      this.opener = null;
      var self = this;

      // Wire the close controls that live inside this drawer.
      qa("[data-drawer-close]", this).forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          self.close();
        });
      });
      if (this.overlay) {
        this.overlay.addEventListener("click", function () {
          self.close();
        });
      }

      // Find every toggle that controls this drawer (by aria-controls or
      // a generic [data-menu-toggle]).
      this._bindToggles();
    };

    Ctor.prototype._bindToggles = function () {
      var self = this;
      var id = this.id;
      var toggles = qa("[data-menu-toggle]");
      toggles.forEach(function (toggle) {
        var controls = toggle.getAttribute("aria-controls");
        if (controls && id && controls !== id) return;
        toggle.addEventListener("click", function (e) {
          e.preventDefault();
          self.open(toggle);
        });
      });
    };

    Ctor.prototype.open = function (opener) {
      this.opener = opener || document.activeElement;
      this.drawerEl.classList.add("is-open");
      this.drawerEl.setAttribute("aria-hidden", "false");
      qa("[data-menu-toggle]").forEach(function (t) {
        t.setAttribute("aria-expanded", "true");
      });
      lockScroll();
      registerOpenDrawer(this);
      trapFocus(this.panel, this);
    };

    Ctor.prototype.close = function () {
      if (!this.drawerEl.classList.contains("is-open")) return;
      this.drawerEl.classList.remove("is-open");
      this.drawerEl.setAttribute("aria-hidden", "true");
      qa("[data-menu-toggle]").forEach(function (t) {
        t.setAttribute("aria-expanded", "false");
      });
      releaseFocus(this.panel, this);
      unlockScroll();
      unregisterOpenDrawer(this);
      if (this.opener && typeof this.opener.focus === "function") {
        this.opener.focus();
      }
    };

    return Ctor;
  })();

  /* --------------------------------------------------------------------------
     4. CartDrawer — <cart-drawer>
     Rendered as a section in layout. open()/close()/renderFromSections().
     -------------------------------------------------------------------------- */
  var CartDrawer = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    if (typeof HTMLElement === "function") {
      Object.setPrototypeOf(Ctor.prototype, HTMLElement.prototype);
      Object.setPrototypeOf(Ctor, HTMLElement);
    }

    Ctor.prototype.connectedCallback = function () {
      this.opener = null;
      this._bindInternals();
    };

    // (Re)bind close + overlay controls — called again after a section swap.
    Ctor.prototype._bindInternals = function () {
      var self = this;
      this.drawerEl = this.querySelector(".drawer") || this;
      this.panel = this.querySelector(".drawer__panel") || this;
      this.overlay = this.querySelector(".drawer__overlay");

      qa("[data-drawer-close]", this).forEach(function (btn) {
        // Avoid double-binding after innerHTML swaps by tagging bound nodes.
        if (btn._cartBound) return;
        btn._cartBound = true;
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          self.close();
        });
      });
      if (this.overlay && !this.overlay._cartBound) {
        this.overlay._cartBound = true;
        this.overlay.addEventListener("click", function () {
          self.close();
        });
      }

      // Quantity changes & remove links inside the drawer hit the AJAX cart.
      this._bindLineItems();
    };

    Ctor.prototype._bindLineItems = function () {
      var self = this;
      qa("[data-cart-remove]", this).forEach(function (link) {
        if (link._cartBound) return;
        link._cartBound = true;
        link.addEventListener("click", function (e) {
          e.preventDefault();
          var key = link.getAttribute("data-cart-remove");
          self.changeLine(key, 0);
        });
      });
      qa("quantity-input[data-line-key]", this).forEach(function (qty) {
        if (qty._cartBound) return;
        qty._cartBound = true;
        var input = qty.querySelector('input[type="number"]');
        if (!input) return;
        var key = qty.getAttribute("data-line-key");
        input.addEventListener(
          "change",
          debounce(function () {
            var value = parseInt(input.value, 10);
            if (isNaN(value) || value < 0) value = 0;
            self.changeLine(key, value);
          }, 300)
        );
      });
    };

    // Change a line item by line key via /cart/change.js, re-render the drawer.
    Ctor.prototype.changeLine = function (key, quantity) {
      if (!key) return;
      this.setLoading(true);
      var body = {
        id: key,
        quantity: quantity,
        sections: "cart-drawer",
        sections_url: window.location.pathname
      };
      var self = this;
      fetch((theme.routes.cart_change_url || "/cart/change") + ".js", fetchConfig("json", body))
        .then(function (res) {
          return res.json();
        })
        .then(function (cart) {
          if (cart && cart.sections) {
            self.renderFromSections(cart.sections);
          }
          setCartCount(cart ? cart.item_count : 0);
        })
        .catch(function () {
          /* leave drawer as-is on failure */
        })
        .then(function () {
          self.setLoading(false);
        });
    };

    Ctor.prototype.setLoading = function (state) {
      this.classList.toggle("is-loading", !!state);
      if (this.panel) this.panel.setAttribute("aria-busy", state ? "true" : "false");
    };

    /*
     * Replace the drawer contents from a Section Rendering response object.
     * Accepts the `sections` map from /cart/add.js, /cart/change.js, etc.
     * Markup is server-rendered and trusted.
     */
    Ctor.prototype.renderFromSections = function (sections) {
      if (!sections || !sections["cart-drawer"]) return;
      var html = sections["cart-drawer"];
      var newDoc = parseHTML(html);
      // The section response wraps another <cart-drawer>; pull its panel.
      var newPanel = newDoc.querySelector(".drawer__panel");
      var currentPanel = this.querySelector(".drawer__panel");
      if (newPanel && currentPanel) {
        currentPanel.innerHTML = newPanel.innerHTML;
      } else {
        // Fallback: swap whole inner markup of the new <cart-drawer>.
        var newRoot = newDoc.querySelector("cart-drawer");
        if (newRoot) this.innerHTML = newRoot.innerHTML;
      }
      this._bindInternals();
    };

    Ctor.prototype.open = function (opener) {
      this.opener = opener || document.activeElement;
      if (!this.drawerEl) this.drawerEl = this.querySelector(".drawer") || this;
      this.drawerEl.classList.add("is-open");
      this.drawerEl.setAttribute("aria-hidden", "false");
      lockScroll();
      registerOpenDrawer(this);
      trapFocus(this.querySelector(".drawer__panel"), this);
    };

    Ctor.prototype.close = function () {
      if (!this.drawerEl) this.drawerEl = this.querySelector(".drawer") || this;
      if (!this.drawerEl.classList.contains("is-open")) return;
      this.drawerEl.classList.remove("is-open");
      this.drawerEl.setAttribute("aria-hidden", "true");
      releaseFocus(this.querySelector(".drawer__panel"), this);
      unlockScroll();
      unregisterOpenDrawer(this);
      if (this.opener && typeof this.opener.focus === "function") {
        this.opener.focus();
      }
    };

    return Ctor;
  })();

  // Convenience accessor used by product forms and the delegated opener.
  function getCartDrawer() {
    return q("cart-drawer");
  }

  /* --------------------------------------------------------------------------
     5. PredictiveSearch — <predictive-search>
     Debounced suggest.json, accessible results, Escape/click-out to close.
     -------------------------------------------------------------------------- */
  var PredictiveSearch = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    if (typeof HTMLElement === "function") {
      Object.setPrototypeOf(Ctor.prototype, HTMLElement.prototype);
      Object.setPrototypeOf(Ctor, HTMLElement);
    }

    Ctor.prototype.connectedCallback = function () {
      this.input = this.querySelector("[data-predictive-input]");
      this.results = this.querySelector("[data-predictive-results]");
      this.form = this.querySelector("form");
      this.activeIndex = -1;
      var self = this;
      if (!this.input || !this.results) return;

      // ARIA combobox semantics.
      this.input.setAttribute("role", "combobox");
      this.input.setAttribute("aria-autocomplete", "list");
      this.input.setAttribute("aria-expanded", "false");
      this.input.setAttribute("autocomplete", "off");
      if (!this.results.id) this.results.id = "predictive-results-" + Math.random().toString(36).slice(2, 8);
      this.input.setAttribute("aria-controls", this.results.id);
      this.results.setAttribute("role", "listbox");

      this._onInput = debounce(function () {
        self.search();
      }, 250);

      this.input.addEventListener("input", this._onInput);
      this.input.addEventListener("keydown", function (e) {
        self.onKeydown(e);
      });

      // Close when focus leaves the whole component.
      document.addEventListener("click", function (e) {
        if (!self.contains(e.target)) self.close();
      });
      this.input.addEventListener("focus", function () {
        if (self.input.value.trim().length > 1 && self.results.childElementCount) {
          self.openResults();
        }
      });
    };

    Ctor.prototype.search = function () {
      var term = this.input.value.trim();
      if (term.length < 2) {
        this.close();
        return;
      }
      var url =
        (theme.routes.predictive_search_url || "/search/suggest") +
        ".json?q=" +
        encodeURIComponent(term) +
        "&resources[type]=product,collection,query&resources[limit]=6";
      var self = this;
      this.setAttribute("aria-busy", "true");
      fetch(url, { headers: { Accept: "application/json" } })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (data) {
          self.setAttribute("aria-busy", "false");
          if (!data || !data.resources || !data.resources.results) {
            self.close();
            return;
          }
          self.render(data.resources.results, term);
        })
        .catch(function () {
          self.setAttribute("aria-busy", "false");
          self.close();
        });
    };

    // Build the results list. Every dynamic value is run through escapeHTML
    // before being placed into the markup string — no XSS surface.
    Ctor.prototype.render = function (results, term) {
      var products = results.products || [];
      var collections = results.collections || [];
      var queries = results.queries || [];
      var html = "";
      var idx = 0;

      if (queries.length) {
        html += '<ul class="predictive__group" role="group" aria-label="Suggestions">';
        queries.forEach(function (item) {
          html +=
            '<li class="predictive__item predictive__item--query" role="option" id="ps-opt-' +
            idx +
            '" data-ps-index="' +
            idx +
            '"><a href="' +
            escapeHTML(item.url) +
            '" tabindex="-1">' +
            escapeHTML(item.text) +
            "</a></li>";
          idx += 1;
        });
        html += "</ul>";
      }

      if (collections.length) {
        html += '<ul class="predictive__group" role="group" aria-label="Collections">';
        collections.forEach(function (item) {
          html +=
            '<li class="predictive__item predictive__item--collection" role="option" id="ps-opt-' +
            idx +
            '" data-ps-index="' +
            idx +
            '"><a href="' +
            escapeHTML(item.url) +
            '" tabindex="-1">' +
            escapeHTML(item.title) +
            "</a></li>";
          idx += 1;
        });
        html += "</ul>";
      }

      if (products.length) {
        html += '<ul class="predictive__group predictive__group--products" role="group" aria-label="Products">';
        products.forEach(function (p) {
          var img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
          var priceText = "";
          if (p.price != null && p.price !== "") {
            // suggest.json returns price in major currency units (e.g. "3000.00");
            // convert to cents so formatMoney applies the shop's money format (lei, grouping).
            var _amt = parseFloat(String(p.price).replace(/[^0-9.\-]/g, ""));
            priceText = isNaN(_amt) ? String(p.price) : formatMoney(Math.round(_amt * 100));
          }
          html +=
            '<li class="predictive__item predictive__item--product" role="option" id="ps-opt-' +
            idx +
            '" data-ps-index="' +
            idx +
            '"><a href="' +
            escapeHTML(p.url) +
            '" tabindex="-1">' +
            (img
              ? '<img class="predictive__img" src="' +
                escapeHTML(img) +
                '" alt="" width="48" height="48" loading="lazy">'
              : '<span class="predictive__img placeholder-svg" aria-hidden="true"></span>') +
            '<span class="predictive__meta"><span class="predictive__title">' +
            escapeHTML(p.title) +
            "</span>" +
            (priceText ? '<span class="predictive__price">' + escapeHTML(priceText) + "</span>" : "") +
            "</span></a></li>";
          idx += 1;
        });
        html += "</ul>";
      }

      if (idx) {
        var allUrl =
          ((this.form && this.form.getAttribute("action")) || "/search") +
          "?q=" +
          encodeURIComponent(term);
        html +=
          '<a class="predictive__all" href="' +
          escapeHTML(allUrl) +
          '">See all results for &ldquo;' +
          escapeHTML(term) +
          '&rdquo;<span aria-hidden="true">&rarr;</span></a>';
      }

      if (!idx) {
        html =
          '<div class="predictive__empty" role="status">No results for &ldquo;' +
          escapeHTML(term) +
          '&rdquo;</div>';
      }

      this.results.innerHTML = html;
      this.activeIndex = -1;
      this.openResults();
    };

    Ctor.prototype.openResults = function () {
      this.results.classList.add("is-open");
      this.results.classList.remove("is-hidden");
      this.results.hidden = false;
      this.input.setAttribute("aria-expanded", "true");
    };

    Ctor.prototype.close = function () {
      if (!this.results) return;
      this.results.classList.remove("is-open");
      this.results.classList.add("is-hidden");
      this.results.hidden = true;
      this.input.setAttribute("aria-expanded", "false");
      this.input.removeAttribute("aria-activedescendant");
      this.activeIndex = -1;
    };

    Ctor.prototype.onKeydown = function (e) {
      var options = qa("[data-ps-index]", this.results);
      if (e.key === "Escape") {
        this.close();
        return;
      }
      if (e.key === "Enter") {
        // If a suggestion is active, follow it; else submit the real form.
        if (this.activeIndex >= 0 && options[this.activeIndex]) {
          var link = options[this.activeIndex].querySelector("a");
          if (link) {
            e.preventDefault();
            window.location.href = link.href;
            return;
          }
        }
        // Otherwise allow native submit of the search form.
        return;
      }
      if (!options.length) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.activeIndex = (this.activeIndex + 1) % options.length;
        this.highlight(options);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.activeIndex = (this.activeIndex - 1 + options.length) % options.length;
        this.highlight(options);
      }
    };

    Ctor.prototype.highlight = function (options) {
      var self = this;
      options.forEach(function (opt, i) {
        var on = i === self.activeIndex;
        opt.classList.toggle("is-active", on);
        opt.setAttribute("aria-selected", on ? "true" : "false");
        if (on) {
          self.input.setAttribute("aria-activedescendant", opt.id);
          opt.scrollIntoView({ block: "nearest" });
        }
      });
    };

    return Ctor;
  })();

  /* --------------------------------------------------------------------------
     6. ProductFormElement — <product-form>
     Intercept submit -> POST /cart/add.js with section rendering, update the
     cart drawer + count, open the drawer, surface errors.
     -------------------------------------------------------------------------- */
  var ProductFormElement = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    if (typeof HTMLElement === "function") {
      Object.setPrototypeOf(Ctor.prototype, HTMLElement.prototype);
      Object.setPrototypeOf(Ctor, HTMLElement);
    }

    Ctor.prototype.connectedCallback = function () {
      this.form = this.querySelector("form");
      if (!this.form) return;
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorTarget = this.querySelector("[data-form-error]") || q("[data-form-error]");
      var self = this;
      this.form.addEventListener("submit", function (e) {
        self.onSubmit(e);
      });
    };

    Ctor.prototype.onSubmit = function (e) {
      e.preventDefault();
      if (this.submitButton && this.submitButton.getAttribute("aria-disabled") === "true") return;

      var self = this;
      var formData = new FormData(this.form);
      // Ensure the cart drawer is re-rendered server-side.
      formData.set("sections", "cart-drawer");
      formData.set("sections_url", window.location.pathname);

      this.setLoading(true);
      this.clearError();

      fetch((theme.routes.cart_add_url || "/cart/add") + ".js", {
        method: "POST",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: formData
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, status: res.status, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok || result.data.status) {
            // Shopify returns { description, message, status } on error.
            self.showError(result.data.description || result.data.message || "Could not add to cart.");
            return;
          }
          var drawer = getCartDrawer();
          if (drawer && result.data.sections) {
            drawer.renderFromSections(result.data.sections);
          }
          // Always reconcile the count from a fresh /cart.js read.
          refreshCartCount().then(function () {
            if (drawer) drawer.open(self.submitButton);
          });
          self.dispatchEvent(
            new CustomEvent("variant:added", { bubbles: true, detail: { item: result.data } })
          );
        })
        .catch(function () {
          self.showError("Something went wrong. Please try again.");
        })
        .then(function () {
          self.setLoading(false);
        });
    };

    Ctor.prototype.setLoading = function (state) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle("is-loading", !!state);
      this.submitButton.setAttribute("aria-busy", state ? "true" : "false");
      this.submitButton.disabled = !!state;
    };

    Ctor.prototype.showError = function (message) {
      if (this.errorTarget) {
        this.errorTarget.textContent = message; // textContent — safe
        this.errorTarget.hidden = false;
      } else {
        // Last resort so the user is never left silent.
        window.alert(message);
      }
    };

    Ctor.prototype.clearError = function () {
      if (this.errorTarget) {
        this.errorTarget.textContent = "";
        this.errorTarget.hidden = true;
      }
    };

    return Ctor;
  })();

  /* --------------------------------------------------------------------------
     7. VariantPicker — <variant-picker>
     Reads data-variants JSON, updates id/price/button/url on change.
     -------------------------------------------------------------------------- */
  var VariantPicker = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    if (typeof HTMLElement === "function") {
      Object.setPrototypeOf(Ctor.prototype, HTMLElement.prototype);
      Object.setPrototypeOf(Ctor, HTMLElement);
    }

    Ctor.prototype.connectedCallback = function () {
      var dataScript = this.querySelector("script[data-variants]");
      this.variants = [];
      if (dataScript) {
        try {
          this.variants = JSON.parse(dataScript.textContent) || [];
        } catch (err) {
          this.variants = [];
        }
      }
      // Locate the associated product form (sibling or document).
      this.productForm =
        this.closest("[data-product-form-root]") ||
        document.getElementById(this.getAttribute("data-form")) ||
        document;
      this.idInput = this.productForm.querySelector('input[name="id"]') || q('input[name="id"]');
      this.priceTarget = q("[data-price]", this.productForm) || q("[data-price]");
      this.submitButton =
        q('[data-product-form] [type="submit"]', this.productForm) ||
        q('product-form [type="submit"]');
      this.availabilityTarget = q("[data-availability]") || null;

      var self = this;
      qa('input[type="radio"], select', this).forEach(function (input) {
        input.addEventListener("change", function () {
          self.onChange();
        });
      });

      // Sync once on load (handles ?variant= deep links done server-side too).
      this.onChange(true);
    };

    // Collect the currently selected option values in order.
    Ctor.prototype.selectedOptions = function () {
      var values = [];
      var groups = {};
      qa('input[type="radio"]:checked', this).forEach(function (radio) {
        groups[radio.name] = radio.value; // name = option1/option2/option3
      });
      qa("select", this).forEach(function (select) {
        groups[select.name] = select.value;
      });
      ["option1", "option2", "option3"].forEach(function (key) {
        if (groups[key] !== undefined) values.push(groups[key]);
      });
      return values;
    };

    Ctor.prototype.findVariant = function () {
      var selected = this.selectedOptions();
      if (!selected.length) {
        // No option selectors are rendered (single / default-variant product).
        // Fall back to the only variant instead of reporting "Unavailable".
        return this.variants[0] || null;
      }
      return (
        this.variants.find(function (variant) {
          return variant.options.every(function (opt, i) {
            return selected[i] === undefined || opt === selected[i];
          });
        }) || null
      );
    };

    Ctor.prototype.onChange = function (initial) {
      var variant = this.findVariant();
      this.currentVariant = variant;

      if (variant && this.idInput) {
        this.idInput.value = variant.id;
      }

      this.updateButton(variant);
      this.updatePrice(variant);
      this.updateAvailability(variant);
      if (!initial) this.updateURL(variant);

      this.dispatchEvent(
        new CustomEvent("variant:change", { bubbles: true, detail: { variant: variant } })
      );
    };

    Ctor.prototype.updateButton = function (variant) {
      if (!this.submitButton) return;
      if (!variant) {
        this.submitButton.disabled = true;
        this.setButtonText("Unavailable");
      } else if (!variant.available) {
        this.submitButton.disabled = true;
        this.setButtonText("Sold out");
      } else {
        this.submitButton.disabled = false;
        this.setButtonText(this.submitButton.getAttribute("data-add-label") || "Add to cart");
      }
    };

    Ctor.prototype.setButtonText = function (text) {
      var span = this.submitButton.querySelector("[data-add-label-text]");
      if (span) span.textContent = text; // textContent — safe
      else this.submitButton.textContent = text;
    };

    // Build price markup from variant cents. compare/price are numeric and
    // pass through formatMoney (not user input).
    Ctor.prototype.updatePrice = function (variant) {
      if (!this.priceTarget || !variant) return;
      var html = '<span class="price__regular">' + formatMoney(variant.price) + "</span>";
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html =
          '<span class="price__sale">' +
          formatMoney(variant.price) +
          '</span> <s class="price__compare">' +
          formatMoney(variant.compare_at_price) +
          "</s>";
        this.priceTarget.classList.add("price--on-sale");
      } else {
        this.priceTarget.classList.remove("price--on-sale");
      }
      this.priceTarget.innerHTML = html;
    };

    Ctor.prototype.updateAvailability = function (variant) {
      if (!this.availabilityTarget) return;
      if (variant && variant.available) {
        this.availabilityTarget.textContent = "In stock — ready to ship";
        this.availabilityTarget.classList.remove("is-unavailable");
      } else {
        this.availabilityTarget.textContent = variant ? "Out of stock" : "Unavailable";
        this.availabilityTarget.classList.add("is-unavailable");
      }
    };

    Ctor.prototype.updateURL = function (variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url.toString());
    };

    return Ctor;
  })();

  /* --------------------------------------------------------------------------
     8. QuantityInput — <quantity-input>
     Minus/plus buttons, clamp at min (and optional max).
     -------------------------------------------------------------------------- */
  var QuantityInput = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    if (typeof HTMLElement === "function") {
      Object.setPrototypeOf(Ctor.prototype, HTMLElement.prototype);
      Object.setPrototypeOf(Ctor, HTMLElement);
    }

    Ctor.prototype.connectedCallback = function () {
      this.input = this.querySelector('input[type="number"]');
      this.minus = this.querySelector('[data-qty="minus"]');
      this.plus = this.querySelector('[data-qty="plus"]');
      if (!this.input) return;
      var self = this;

      if (this.minus) {
        this.minus.addEventListener("click", function (e) {
          e.preventDefault();
          self.step(-1);
        });
      }
      if (this.plus) {
        this.plus.addEventListener("click", function (e) {
          e.preventDefault();
          self.step(1);
        });
      }
      this.input.addEventListener("change", function () {
        self.clamp();
      });
    };

    Ctor.prototype.step = function (direction) {
      var current = parseInt(this.input.value, 10);
      if (isNaN(current)) current = this.getMin();
      var stepBy = parseInt(this.input.getAttribute("step"), 10) || 1;
      this.input.value = current + direction * stepBy;
      this.clamp();
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
    };

    Ctor.prototype.getMin = function () {
      var min = parseInt(this.input.getAttribute("min"), 10);
      return isNaN(min) ? 0 : min;
    };

    Ctor.prototype.clamp = function () {
      var value = parseInt(this.input.value, 10);
      var min = this.getMin();
      var maxAttr = this.input.getAttribute("max");
      var max = maxAttr !== null ? parseInt(maxAttr, 10) : null;
      if (isNaN(value)) value = min;
      if (value < min) value = min;
      if (max !== null && !isNaN(max) && value > max) value = max;
      this.input.value = value;
    };

    return Ctor;
  })();

  /* --------------------------------------------------------------------------
     9. FacetFilters — <facet-filters>
     Progressive enhancement using the Section Rendering API.
     Works without JS (method=get). With JS: fetch &section_id=, swap grid.
     -------------------------------------------------------------------------- */
  var FacetFilters = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    if (typeof HTMLElement === "function") {
      Object.setPrototypeOf(Ctor.prototype, HTMLElement.prototype);
      Object.setPrototypeOf(Ctor, HTMLElement);
    }

    Ctor.prototype.connectedCallback = function () {
      this.form = this.querySelector("[data-facet-form]");
      this.sortSelect = this.querySelector("[data-sort]");
      this.sectionId = this.getAttribute("data-section-id") || (this.dataset ? this.dataset.sectionId : "");
      var self = this;

      if (this.form) {
        // Change on any checkbox immediately re-renders.
        this.form.addEventListener("change", function (e) {
          // Price inputs use a debounced 'input' path instead.
          if (e.target && (e.target.type === "text" || e.target.type === "number")) return;
          self.apply();
        });
        // Submit (Enter on price inputs, or no-JS fallback) also applies.
        this.form.addEventListener("submit", function (e) {
          e.preventDefault();
          self.apply();
        });
        // Debounced price typing.
        qa('input[type="number"], input[type="text"]', this.form).forEach(function (input) {
          input.addEventListener(
            "input",
            debounce(function () {
              self.apply();
            }, 500)
          );
        });
      }

      if (this.sortSelect) {
        this.sortSelect.addEventListener("change", function () {
          self.apply();
        });
      }

      // Mobile "Filters" toggle.
      var toggle = q("[data-facets-toggle]");
      if (toggle) {
        toggle.addEventListener("click", function () {
          self.classList.toggle("is-open");
          var expanded = self.classList.contains("is-open");
          toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
          if (expanded) lockScroll();
          else unlockScroll();
        });
      }
      var mobileClose = q("[data-facets-close]", this);
      if (mobileClose) {
        mobileClose.addEventListener("click", function () {
          self.classList.remove("is-open");
          unlockScroll();
        });
      }

      // Re-apply when the user navigates back/forward.
      window.addEventListener("popstate", function () {
        self.applyFromURL();
      });

      // Delegate clicks on active-filter "remove" chips (they are links).
      this.addEventListener("click", function (e) {
        var chip = e.target.closest ? e.target.closest("[data-facet-remove]") : null;
        if (!chip) return;
        e.preventDefault();
        self.navigate(chip.getAttribute("href"));
      });
    };

    // Build a query string from the form state.
    Ctor.prototype.buildURL = function () {
      var params = new URLSearchParams();
      if (this.form) {
        var formData = new FormData(this.form);
        formData.forEach(function (value, key) {
          if (value === "" || value === null) return;
          params.append(key, value);
        });
      }
      if (this.sortSelect && this.sortSelect.value) {
        params.set("sort_by", this.sortSelect.value);
      }
      var base = window.location.pathname;
      var qs = params.toString();
      return qs ? base + "?" + qs : base;
    };

    Ctor.prototype.apply = function () {
      var url = this.buildURL();
      this.fetchAndSwap(url, true);
    };

    Ctor.prototype.applyFromURL = function () {
      this.fetchAndSwap(window.location.href, false);
    };

    Ctor.prototype.navigate = function (url) {
      if (!url) return;
      this.fetchAndSwap(url, true);
    };

    Ctor.prototype.fetchAndSwap = function (url, push) {
      if (!url) return;
      var self = this;
      // Build the section-rendering URL.
      var sep = url.indexOf("?") === -1 ? "?" : "&";
      var fetchURL = url + (this.sectionId ? sep + "section_id=" + encodeURIComponent(this.sectionId) : "");

      this.setLoading(true);
      fetchSection(fetchURL).then(function (text) {
        if (text) {
          replaceProductGrid(text, document);
          self.refreshFacetsFromDoc(text);
        }
        if (push && window.history && window.history.pushState) {
          window.history.pushState({}, "", url);
        }
        self.setLoading(false);
        // Newly injected quantity / form elements upgrade automatically via
        // customElements; nothing else to do here.
        self.scrollToGridTop();
      });
    };

    // Replace the facet form internals (counts, active chips) from response.
    // Source is the trusted Section Rendering response.
    Ctor.prototype.refreshFacetsFromDoc = function (text) {
      var newDoc = parseHTML(text);
      var newFacets = newDoc.querySelector("[data-facets]");
      if (!newFacets) return;
      var newActive = newFacets.querySelector("[data-facets-active]");
      var oldActive = this.querySelector("[data-facets-active]");
      if (newActive && oldActive) oldActive.innerHTML = newActive.innerHTML;
      // Update result count if present.
      var newCount = newFacets.querySelector("[data-results-count]");
      var oldCount = this.querySelector("[data-results-count]");
      if (newCount && oldCount) oldCount.textContent = newCount.textContent;
    };

    Ctor.prototype.scrollToGridTop = function () {
      var grid = q("[data-product-grid]");
      if (!grid) return;
      var top = grid.getBoundingClientRect().top + window.pageYOffset - 100;
      if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: "smooth" });
    };

    Ctor.prototype.setLoading = function (state) {
      this.classList.toggle("is-loading", !!state);
      var grid = q("[data-product-grid]");
      if (grid) grid.setAttribute("aria-busy", state ? "true" : "false");
    };

    return Ctor;
  })();

  /* --------------------------------------------------------------------------
     10. Slideshow — <slideshow-component>
     Autoplay, dots, prev/next, pause on hover, prefers-reduced-motion aware.
     -------------------------------------------------------------------------- */
  var Slideshow = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    if (typeof HTMLElement === "function") {
      Object.setPrototypeOf(Ctor.prototype, HTMLElement.prototype);
      Object.setPrototypeOf(Ctor, HTMLElement);
    }

    Ctor.prototype.connectedCallback = function () {
      this.slides = qa("[data-slide]", this);
      this.prevBtn = this.querySelector("[data-slide-prev]");
      this.nextBtn = this.querySelector("[data-slide-next]");
      this.dotsWrap = this.querySelector("[data-slide-dots]");
      this.index = 0;
      this.timer = null;
      this.reducedMotion =
        window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.autoplay = this.getAttribute("data-autoplay") === "true" && !this.reducedMotion;
      this.speed = (parseFloat(this.getAttribute("data-speed")) || 6) * 1000;

      if (this.slides.length <= 1) {
        this.toggleControls(false);
        return;
      }
      var self = this;

      if (this.prevBtn) {
        this.prevBtn.addEventListener("click", function () {
          self.go(self.index - 1, true);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener("click", function () {
          self.go(self.index + 1, true);
        });
      }
      this.buildDots();
      this.show(0);

      // Pause on hover / focus.
      this.addEventListener("mouseenter", function () {
        self.stop();
      });
      this.addEventListener("mouseleave", function () {
        self.start();
      });
      this.addEventListener("focusin", function () {
        self.stop();
      });
      this.addEventListener("focusout", function () {
        self.start();
      });

      // Keyboard arrows when the slideshow has focus.
      this.addEventListener("keydown", function (e) {
        if (e.key === "ArrowLeft") {
          self.go(self.index - 1, true);
        } else if (e.key === "ArrowRight") {
          self.go(self.index + 1, true);
        }
      });

      // Basic swipe support.
      this._bindSwipe();

      this.start();
    };

    Ctor.prototype.disconnectedCallback = function () {
      this.stop();
    };

    Ctor.prototype.buildDots = function () {
      if (!this.dotsWrap) return;
      var self = this;
      this.dotsWrap.innerHTML = "";
      this.slides.forEach(function (slide, i) {
        var dot = document.createElement("button");
        dot.type = "button";
        dot.className = "slideshow__dot";
        dot.setAttribute("aria-label", "Go to slide " + (i + 1));
        dot.addEventListener("click", function () {
          self.go(i, true);
        });
        self.dotsWrap.appendChild(dot);
      });
      this.dots = qa(".slideshow__dot", this.dotsWrap);
    };

    Ctor.prototype.show = function (i) {
      this.slides.forEach(function (slide, idx) {
        var active = idx === i;
        slide.classList.toggle("is-active", active);
        slide.setAttribute("aria-hidden", active ? "false" : "true");
        // Keep off-screen slides out of the tab order.
        qa("a, button, input", slide).forEach(function (el) {
          if (active) el.removeAttribute("tabindex");
          else el.setAttribute("tabindex", "-1");
        });
      });
      if (this.dots) {
        this.dots.forEach(function (dot, idx) {
          var on = idx === i;
          dot.classList.toggle("is-active", on);
          dot.setAttribute("aria-current", on ? "true" : "false");
        });
      }
      this.index = i;
    };

    Ctor.prototype.go = function (i, manual) {
      var total = this.slides.length;
      var next = ((i % total) + total) % total; // wrap both directions
      this.show(next);
      if (manual) {
        // Reset autoplay timer after a manual interaction.
        this.stop();
        this.start();
      }
    };

    Ctor.prototype.start = function () {
      if (!this.autoplay || this.slides.length <= 1) return;
      var self = this;
      this.stop();
      this.timer = window.setInterval(function () {
        self.go(self.index + 1, false);
      }, this.speed);
    };

    Ctor.prototype.stop = function () {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    };

    Ctor.prototype.toggleControls = function (show) {
      [this.prevBtn, this.nextBtn, this.dotsWrap].forEach(function (el) {
        if (el) el.hidden = !show;
      });
    };

    Ctor.prototype._bindSwipe = function () {
      var self = this;
      var startX = 0;
      var moved = false;
      this.addEventListener(
        "touchstart",
        function (e) {
          startX = e.touches[0].clientX;
          moved = false;
        },
        { passive: true }
      );
      this.addEventListener(
        "touchmove",
        function () {
          moved = true;
        },
        { passive: true }
      );
      this.addEventListener(
        "touchend",
        function (e) {
          if (!moved) return;
          var delta = e.changedTouches[0].clientX - startX;
          if (Math.abs(delta) < 40) return;
          self.go(self.index + (delta < 0 ? 1 : -1), true);
        },
        { passive: true }
      );
    };

    return Ctor;
  })();

  /* --------------------------------------------------------------------------
     11. Register all custom elements (guarded)
     -------------------------------------------------------------------------- */
  function define(name, ctor) {
    try {
      if (window.customElements && !customElements.get(name)) {
        customElements.define(name, ctor);
      }
    } catch (err) {
      /* never throw during element registration */
    }
  }

  if (window.customElements && typeof Reflect !== "undefined" && Reflect.construct) {
    define("menu-drawer", MenuDrawer);
    define("cart-drawer", CartDrawer);
    define("predictive-search", PredictiveSearch);
    define("product-form", ProductFormElement);
    define("variant-picker", VariantPicker);
    define("quantity-input", QuantityInput);
    define("facet-filters", FacetFilters);
    define("slideshow-component", Slideshow);
  }

  /* --------------------------------------------------------------------------
     12. Global delegated handlers
     - [data-cart-open] opens the cart drawer.
     - [data-drawer-close] / overlay click closes the nearest drawer.
     - Escape closes the topmost open drawer.
     -------------------------------------------------------------------------- */
  document.addEventListener("click", function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    // Open cart drawer.
    var cartOpener = target.closest("[data-cart-open]");
    if (cartOpener) {
      var drawer = getCartDrawer();
      if (drawer && typeof drawer.open === "function") {
        e.preventDefault();
        drawer.open(cartOpener);
        return;
      }
    }

    // Generic close button anywhere (delegated safety net).
    var closer = target.closest("[data-drawer-close]");
    if (closer) {
      var ownDrawer = closer.closest("menu-drawer, cart-drawer, .drawer");
      if (ownDrawer && typeof ownDrawer.close === "function") {
        e.preventDefault();
        ownDrawer.close();
        return;
      }
      // Fallback: close the topmost registered drawer.
      var top = openDrawers[openDrawers.length - 1];
      if (top && typeof top.close === "function") {
        e.preventDefault();
        top.close();
      }
    }

    // Overlay click safety net (drawers wire their own too).
    if (target.classList && target.classList.contains("drawer__overlay")) {
      var overlayDrawer = target.closest("menu-drawer, cart-drawer, .drawer");
      if (overlayDrawer && typeof overlayDrawer.close === "function") {
        overlayDrawer.close();
      }
    }
  });

  // Escape closes the topmost open drawer.
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape" && e.key !== "Esc") return;
    var top = openDrawers[openDrawers.length - 1];
    if (top && typeof top.close === "function") {
      top.close();
    }
  });

  /* --------------------------------------------------------------------------
     13. Header mega-menu accessibility helper (hover + focus/click)
     The mega-menu snippet renders .nav__item with a .mega panel. CSS handles
     hover; here we add keyboard/click support so it is fully accessible.
     -------------------------------------------------------------------------- */
  function initMegaMenu() {
    qa(".nav__item").forEach(function (item) {
      var trigger = item.querySelector(".nav__link");
      var mega = item.querySelector(".mega");
      if (!trigger || !mega) return;

      trigger.setAttribute("aria-haspopup", "true");
      trigger.setAttribute("aria-expanded", "false");

      function openItem() {
        item.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
      }
      function closeItem() {
        item.classList.remove("is-open");
        trigger.setAttribute("aria-expanded", "false");
      }

      // Click toggles on touch/no-hover devices.
      trigger.addEventListener("click", function (e) {
        // Allow following the link if it is already open (second tap).
        if (!item.classList.contains("is-open")) {
          e.preventDefault();
          qa(".nav__item.is-open").forEach(function (other) {
            if (other !== item) other.classList.remove("is-open");
          });
          openItem();
        }
      });

      item.addEventListener("focusin", openItem);
      item.addEventListener("focusout", function () {
        // Close only when focus leaves the whole item.
        window.setTimeout(function () {
          if (!item.contains(document.activeElement)) closeItem();
        }, 10);
      });
      item.addEventListener("mouseleave", closeItem);
      item.addEventListener("keydown", function (e) {
        if (e.key === "Escape") {
          closeItem();
          trigger.focus();
        }
      });
    });

    // Click outside closes any open mega panel.
    document.addEventListener("click", function (e) {
      qa(".nav__item.is-open").forEach(function (item) {
        if (!item.contains(e.target)) item.classList.remove("is-open");
      });
    });
  }

  /* --------------------------------------------------------------------------
     14. Product gallery thumb-swap (self-contained fallback)
     The product-gallery snippet may rely on this: any [data-gallery-thumb]
     with data-full swaps the [data-gallery-main] image source.
     -------------------------------------------------------------------------- */
  function initGalleries() {
    qa("[data-media-gallery]").forEach(function (gallery) {
      if (gallery.dataset.bound === "true") return; // already handled by the snippet's inline script
      var main = gallery.querySelector("[data-gallery-main]");
      var thumbs = qa("[data-gallery-thumb]", gallery);
      if (!main || !thumbs.length) return;

      thumbs.forEach(function (thumb) {
        thumb.addEventListener("click", function (e) {
          e.preventDefault();
          var fullSrc = thumb.getAttribute("data-full") || thumb.getAttribute("href");
          var img = main.querySelector("img") || main;
          if (fullSrc && img.tagName === "IMG") {
            img.src = fullSrc;
            var srcset = thumb.getAttribute("data-srcset");
            if (srcset) img.srcset = srcset;
            var alt = thumb.getAttribute("data-alt");
            if (alt !== null) img.alt = alt;
          }
          thumbs.forEach(function (t) {
            t.classList.remove("is-active");
            t.setAttribute("aria-current", "false");
          });
          thumb.classList.add("is-active");
          thumb.setAttribute("aria-current", "true");
        });
      });
    });
  }

  /* --------------------------------------------------------------------------
     14b. Reveal-on-scroll animations
     When <body> opts in with the .animations-reveal class (set by theme.liquid
     from the animations_reveal setting), observe every [data-reveal] element
     and add .is-in-view as it enters the viewport. Entirely defensive:
       - Does nothing if the body class is absent.
       - Respects prefers-reduced-motion (reveals everything immediately).
       - Falls back to revealing everything if IntersectionObserver is missing.
     -------------------------------------------------------------------------- */
  function initRevealAnimations() {
    var body = document.body;
    if (!body || !body.classList.contains("animations-reveal")) return;

    var targets = qa("[data-reveal]");
    if (!targets.length) return;

    var reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // No IO support or reduced motion -> just show everything, no animation.
    if (reduce || typeof window.IntersectionObserver !== "function") {
      targets.forEach(function (el) {
        el.classList.add("is-in-view");
      });
      return;
    }

    var observer = new window.IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in-view");
            obs.unobserve(entry.target); // reveal once, then stop watching
          }
        });
      },
      { root: null, rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* --------------------------------------------------------------------------
     15. Boot
     -------------------------------------------------------------------------- */
  function init() {
    initMegaMenu();
    initGalleries();
    initRevealAnimations();
    // Reconcile cart count once on load (cheap, keeps bubbles in sync).
    if (q("[data-cart-count]")) refreshCartCount();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* ==========================================================================
     VEVOR ENHANCEMENTS
     Self-contained block implementing the Vevor-parity JS contracts:
       #2  Scoped search   -> [data-search-scope] change navigates to value
       #4a Quick view      -> [data-quick-view] fetches main-product section
       #4b Wishlist        -> localStorage "aledo:wishlist" + #WishlistDrawer
       #6a Sticky-condense -> .header--condensed on [data-header] past scroll
       #6b Recently viewed -> localStorage "aledo:recent" -> .rv-card grid
     Everything below is defensive: every lookup is guarded, listeners are
     delegated where practical, and nothing throws when elements are absent.
     Reuses the existing helpers above (q/qa/escapeHTML/lockScroll/etc.).
     ========================================================================== */
  (function vevorEnhancements() {
    /* ---- tiny localStorage helpers (never throw on private mode/quota) ---- */
    function readStore(key) {
      try {
        var raw = window.localStorage.getItem(key);
        if (!raw) return [];
        var parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    function writeStore(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* ignore quota / disabled storage */
      }
    }

    /* ======================================================================
       #2 SCOPED SEARCH — category dropdown navigates to the collection.
       ====================================================================== */
    document.addEventListener("change", function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var scope = target.closest("[data-search-scope]");
      if (!scope) return;
      var value = scope.value;
      if (value) {
        window.location.href = value;
      }
    });

    /* ======================================================================
       #4a QUICK VIEW — fetch main-product section, inject, open #QuickView.
       ====================================================================== */
    var QuickViewModal = (function () {
      function Ctor() {
        return Reflect.construct(HTMLElement, [], Ctor);
      }
      if (typeof HTMLElement === "function") {
        Object.setPrototypeOf(Ctor.prototype, HTMLElement.prototype);
        Object.setPrototypeOf(Ctor, HTMLElement);
      }

      Ctor.prototype.connectedCallback = function () {
        var self = this;
        this.content = this.querySelector("[data-qv-content]");
        this.opener = null;

        // Close controls (overlay + close button) live inside the modal.
        qa("[data-qv-close]", this).forEach(function (btn) {
          if (btn._qvBound) return;
          btn._qvBound = true;
          btn.addEventListener("click", function (ev) {
            ev.preventDefault();
            self.close();
          });
        });
      };

      Ctor.prototype.open = function (opener) {
        this.opener = opener || document.activeElement;
        this.classList.add("is-open");
        this.setAttribute("aria-hidden", "false");
        lockScroll();
        registerOpenDrawer(this);
        trapFocus(this.querySelector(".qv__dialog"), this);
      };

      Ctor.prototype.close = function () {
        if (!this.classList.contains("is-open")) return;
        this.classList.remove("is-open");
        this.setAttribute("aria-hidden", "true");
        releaseFocus(this.querySelector(".qv__dialog"), this);
        unlockScroll();
        unregisterOpenDrawer(this);
        // Clear injected markup so stale custom elements are torn down.
        if (this.content) this.content.innerHTML = "";
        if (this.opener && typeof this.opener.focus === "function") {
          this.opener.focus();
        }
      };

      // Inject server-rendered (trusted) markup, then open.
      Ctor.prototype.load = function (html, opener) {
        if (!this.content) this.content = this.querySelector("[data-qv-content]");
        if (this.content) this.content.innerHTML = html; // trusted Section API HTML
        this.open(opener);
      };

      return Ctor;
    })();

    if (window.customElements && typeof Reflect !== "undefined" && Reflect.construct) {
      if (!customElements.get("quick-view-modal")) {
        try {
          customElements.define("quick-view-modal", QuickViewModal);
        } catch (e) {
          /* ignore */
        }
      }
    }

    document.addEventListener("click", function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var trigger = target.closest("[data-quick-view]");
      if (!trigger) return;
      e.preventDefault();

      var url = trigger.getAttribute("data-product-url");
      if (!url) return;
      var modal = q("#QuickView");
      if (!modal) return;

      var sep = url.indexOf("?") > -1 ? "&" : "?";
      var fetchURL = url + sep + "section_id=main-product";

      trigger.classList.add("is-loading");
      fetchSection(fetchURL).then(function (text) {
        trigger.classList.remove("is-loading");
        if (!text) return;
        if (typeof modal.load === "function") {
          modal.load(text, trigger);
        }
      });
    });

    /* ======================================================================
       #4b WISHLIST — localStorage "aledo:wishlist" + #WishlistDrawer.
       Each saved item: { handle, title, url, image, price }.
       ====================================================================== */
    var WISHLIST_KEY = "aledo:wishlist";

    function getWishlist() {
      return readStore(WISHLIST_KEY);
    }
    function setWishlist(items) {
      writeStore(WISHLIST_KEY, items);
    }
    function wishlistHas(handle) {
      return getWishlist().some(function (item) {
        return item && item.handle === handle;
      });
    }

    function toggleWishlist(payload) {
      if (!payload || !payload.handle) return false;
      var items = getWishlist();
      var idx = -1;
      for (var i = 0; i < items.length; i++) {
        if (items[i] && items[i].handle === payload.handle) {
          idx = i;
          break;
        }
      }
      var added;
      if (idx > -1) {
        items.splice(idx, 1);
        added = false;
      } else {
        items.unshift(payload);
        added = true;
      }
      setWishlist(items);
      return added;
    }

    function removeWishlist(handle) {
      var items = getWishlist().filter(function (item) {
        return !(item && item.handle === handle);
      });
      setWishlist(items);
    }

    // Sync every [data-wishlist] button's .is-active to current storage.
    function syncWishlistButtons() {
      qa("[data-wishlist]").forEach(function (btn) {
        var handle = btn.getAttribute("data-handle");
        var active = handle && wishlistHas(handle);
        btn.classList.toggle("is-active", !!active);
        btn.setAttribute("aria-pressed", active ? "true" : "false");
      });
    }

    // Update [data-wishlist-count] bubbles (hidden when empty).
    function syncWishlistCount() {
      var count = getWishlist().length;
      qa("[data-wishlist-count]").forEach(function (el) {
        el.textContent = String(count);
        el.hidden = count <= 0;
      });
    }

    // Render the saved items into [data-wishlist-items].
    function renderWishlistItems() {
      var container = q("[data-wishlist-items]");
      if (!container) return;
      var items = getWishlist();
      if (!items.length) {
        container.innerHTML =
          '<p class="wishlist-empty" role="status">Your wishlist is empty.</p>';
        return;
      }
      var html = '<ul class="wishlist-list">';
      items.forEach(function (item) {
        if (!item || !item.handle) return;
        html +=
          '<li class="wishlist-item" data-wishlist-row="' +
          escapeHTML(item.handle) +
          '">' +
          (item.image
            ? '<a class="wishlist-item__thumb" href="' +
              escapeHTML(item.url || "#") +
              '"><img src="' +
              escapeHTML(item.image) +
              '" alt="" width="64" height="64" loading="lazy"></a>'
            : '<span class="wishlist-item__thumb placeholder-svg" aria-hidden="true"></span>') +
          '<div class="wishlist-item__meta">' +
          '<a class="wishlist-item__title" href="' +
          escapeHTML(item.url || "#") +
          '">' +
          escapeHTML(item.title || "") +
          "</a>" +
          (item.price
            ? '<span class="wishlist-item__price">' + escapeHTML(item.price) + "</span>"
            : "") +
          "</div>" +
          '<button class="wishlist-item__remove" type="button" data-wishlist-remove data-handle="' +
          escapeHTML(item.handle) +
          '" aria-label="Remove from wishlist">&times;</button>' +
          "</li>";
      });
      html += "</ul>";
      container.innerHTML = html; // all dynamic values escaped above
    }

    function refreshWishlistUI() {
      syncWishlistButtons();
      syncWishlistCount();
      renderWishlistItems();
    }

    // Delegated toggle on product-card hearts.
    document.addEventListener("click", function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var btn = target.closest("[data-wishlist]");
      if (!btn) return;
      e.preventDefault();
      var payload = {
        handle: btn.getAttribute("data-handle") || "",
        title: btn.getAttribute("data-title") || "",
        url: btn.getAttribute("data-url") || "",
        image: btn.getAttribute("data-image") || "",
        price: btn.getAttribute("data-price") || ""
      };
      if (!payload.handle) return;
      toggleWishlist(payload);
      refreshWishlistUI();
    });

    // Delegated remove inside the drawer.
    document.addEventListener("click", function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var rm = target.closest("[data-wishlist-remove]");
      if (!rm) return;
      e.preventDefault();
      var handle = rm.getAttribute("data-handle");
      if (!handle) return;
      removeWishlist(handle);
      refreshWishlistUI();
    });

    // Open the wishlist drawer. Reuse the existing drawer open/close plumbing.
    document.addEventListener("click", function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var opener = target.closest("[data-wishlist-open]");
      if (!opener) return;
      e.preventDefault();
      var drawer = q("#WishlistDrawer");
      if (!drawer) return;
      renderWishlistItems();

      // If a custom element/handler already supplies open(), use it.
      if (typeof drawer.open === "function") {
        drawer.open(opener);
        return;
      }
      // Local fallback that mirrors the drawer helpers above.
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      lockScroll();
      registerOpenDrawer(drawer);

      // Wire close controls once (the global delegated handler also closes
      // [data-drawer-close]/overlay, but provide a defensive local close).
      if (!drawer._wishlistBound) {
        drawer._wishlistBound = true;
        var closeDrawer = function () {
          if (!drawer.classList.contains("is-open")) return;
          drawer.classList.remove("is-open");
          drawer.setAttribute("aria-hidden", "true");
          unlockScroll();
          unregisterOpenDrawer(drawer);
          if (opener && typeof opener.focus === "function") opener.focus();
        };
        drawer.close = closeDrawer;
        qa("[data-drawer-close]", drawer).forEach(function (btn) {
          btn.addEventListener("click", function (ev) {
            ev.preventDefault();
            closeDrawer();
          });
        });
      }
      trapFocus(drawer.querySelector(".drawer__panel"), drawer);
    });

    /* ======================================================================
       #6a STICKY-CONDENSE — shrink the sticky header after scrolling 120px.
       ====================================================================== */
    (function stickyCondense() {
      var header = q("[data-header]");
      if (!header) return;
      var ticking = false;
      function update() {
        ticking = false;
        var condensed = (window.pageYOffset || document.documentElement.scrollTop || 0) > 120;
        header.classList.toggle("header--condensed", condensed);
      }
      window.addEventListener(
        "scroll",
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(update);
        },
        { passive: true }
      );
      update(); // initial state (deep links / reload mid-page)
    })();

    /* ======================================================================
       #6b RECENTLY VIEWED — track viewed products, render a row.
       ====================================================================== */
    var RECENT_KEY = "aledo:recent";
    var RECENT_CAP = 8;

    function trackCurrentProduct() {
      var hook = q("[data-current-product]");
      if (!hook) return null;
      var current;
      try {
        current = JSON.parse(hook.textContent || "{}");
      } catch (e) {
        return null;
      }
      if (!current || !current.handle) return current || null;

      var items = readStore(RECENT_KEY).filter(function (item) {
        return item && item.handle && item.handle !== current.handle;
      });
      items.unshift(current);
      if (items.length > RECENT_CAP) items = items.slice(0, RECENT_CAP);
      writeStore(RECENT_KEY, items);
      return current;
    }

    function renderRecentlyViewed(currentHandle) {
      var section = q("[data-recently-viewed]");
      if (!section) return;
      var grid = q("[data-recently-viewed-grid]", section) || q("[data-recently-viewed-grid]");
      if (!grid) return;

      var items = readStore(RECENT_KEY).filter(function (item) {
        return item && item.handle && item.handle !== currentHandle;
      });
      if (!items.length) {
        section.hidden = true;
        return;
      }

      var html = "";
      items.forEach(function (item) {
        html +=
          '<a class="rv-card" href="' +
          escapeHTML(item.url || "#") +
          '">' +
          (item.image
            ? '<span class="rv-card__media"><img src="' +
              escapeHTML(item.image) +
              '" alt="" loading="lazy"></span>'
            : '<span class="rv-card__media placeholder-svg" aria-hidden="true"></span>') +
          '<span class="rv-card__title">' +
          escapeHTML(item.title || "") +
          "</span>" +
          (item.price
            ? '<span class="rv-card__price">' + escapeHTML(item.price) + "</span>"
            : "") +
          "</a>";
      });
      grid.innerHTML = html; // all dynamic values escaped above
      section.hidden = false;
    }

    /* ---- boot the enhancements once the DOM is ready ---- */
    function bootVevor() {
      // Wishlist UI reflects stored state on every page.
      refreshWishlistUI();
      // Recently viewed: record current product, then render the row.
      var current = trackCurrentProduct();
      renderRecentlyViewed(current ? current.handle : null);
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", bootVevor);
    } else {
      bootVevor();
    }
  })();

  // Expose a small public surface for sections that want to reuse helpers.
  theme.utils = {
    q: q,
    qa: qa,
    escapeHTML: escapeHTML,
    debounce: debounce,
    fetchConfig: fetchConfig,
    formatMoney: formatMoney,
    fetchSection: fetchSection,
    replaceProductGrid: replaceProductGrid,
    refreshCartCount: refreshCartCount,
    getCartDrawer: getCartDrawer,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll
  };
})();
